/**
 * Farm & Acre Co - Variant Auto-Select
 *
 * For NLIS products where Shopify variants encode:
 *   Option 1: NLIS Colour (cattle) or Year Colour (sheep/goat)
 *   Option 2: Qty Price Break tier (or State Pricing if no qty break)
 *   Option 3: State Pricing (if product has qty break + state)
 *
 * This script:
 * 1. Reads all variant data from the #AllflexVariantData JSON script tag
 * 2. Detects option layout (which option is qty break, which is state pricing)
 * 3. Listens for colour radio changes (data-variant-option="nlis-colour")
 * 4. Listens for quantity input changes
 * 5. Listens for state select changes (for state-based pricing)
 * 6. Maps quantity to the correct Qty Break tier
 * 7. Maps state to the correct State Pricing tier
 * 8. Finds the matching variant
 * 9. Updates the hidden input[name="id"] (variant ID for cart)
 * 10. Updates the displayed price
 * 11. Populates volume discount tier table
 */

(function () {
  'use strict';

  var variants = [];
  var qtyBreakTiers = [];
  var currentColour = '';
  var currentQty = 1;
  var currentState = '';

  // Option layout detection
  var STATE_VALUES = ['Standard', 'TAS', 'SA', 'WA'];
  var hasStatePricing = false;
  var stateOptionKey = '';  // 'option2' or 'option3'
  var qtyOptionKey = '';    // 'option2' or '' (empty if no qty break)

  function init() {
    var dataEl = document.getElementById('AllflexVariantData');
    if (!dataEl) return;

    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    if (variants.length === 0) return;

    // Detect option layout
    detectOptionLayout();

    // Extract unique qty break tiers from the correct option
    extractQtyBreakTiers();

    // Populate the volume discount table
    populateVolumeTiers();

    // Listen for the primary (option1) selector: NLIS/Year colour OR Tag Size.
    // Both drive option1 of the variant grid; a product uses one or the other, never both,
    // so a combined query is safe. `currentColour` holds the option1 match value regardless.
    var colourRadios = document.querySelectorAll('[data-variant-option="nlis-colour"], [data-variant-option="size"]');
    colourRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        currentColour = this.value;
        updateVariant();
        populateVolumeTiers();
      });
      // Set initial colour from checked radio
      if (radio.checked) {
        currentColour = radio.value;
      }
    });

    // Listen for quantity changes (debounced for manual typing)
    var qtyInput = document.querySelector('.quantity__input[form]');
    var qtyDebounceTimer = null;
    if (qtyInput) {
      var onQtyChange = function () {
        currentQty = parseInt(qtyInput.value) || 1;
        updateVariant();
      };
      var onQtyInput = function () {
        clearTimeout(qtyDebounceTimer);
        qtyDebounceTimer = setTimeout(function () {
          currentQty = parseInt(qtyInput.value) || 1;
          updateVariant();
        }, 400);
      };
      qtyInput.addEventListener('change', onQtyChange);
      qtyInput.addEventListener('input', onQtyInput);
      currentQty = parseInt(qtyInput.value) || 1;
    }

    // Also listen for Dawn's quantity-input button clicks
    document.addEventListener('change', function (e) {
      if (e.target.closest('quantity-input') && e.target.classList.contains('quantity__input')) {
        currentQty = parseInt(e.target.value) || 1;
        updateVariant();
      }
    });

    // Listen for state select changes (for state-based pricing)
    if (hasStatePricing) {
      var stateSelect = document.getElementById('allflex-state');
      if (stateSelect) {
        stateSelect.addEventListener('change', function () {
          currentState = this.value;
          updateVariant();
          populateVolumeTiers();
        });
        currentState = stateSelect.value || '';
      }
    }

    // Initial update
    updateVariant();
  }

  /**
   * Detect which option positions contain qty break vs state pricing.
   * Checks option2 and option3 values against known state pricing values.
   */
  function detectOptionLayout() {
    var first = variants[0];
    if (!first) return;

    if (first.option3 && STATE_VALUES.indexOf(first.option3) >= 0) {
      // 3-option product: option1=colour, option2=qty, option3=state
      hasStatePricing = true;
      stateOptionKey = 'option3';
      qtyOptionKey = 'option2';
    } else if (first.option2 && STATE_VALUES.indexOf(first.option2) >= 0) {
      // 2-option product: option1=colour, option2=state (no qty break)
      hasStatePricing = true;
      stateOptionKey = 'option2';
      qtyOptionKey = '';
    } else {
      // No state pricing: option1=colour, option2=qty (or nothing)
      hasStatePricing = false;
      stateOptionKey = '';
      qtyOptionKey = first.option2 ? 'option2' : '';
    }
  }

  function extractQtyBreakTiers() {
    if (!qtyOptionKey) return;

    var seen = {};
    for (var i = 0; i < variants.length; i++) {
      var opt = variants[i][qtyOptionKey];
      if (opt && !seen[opt]) {
        seen[opt] = true;
        qtyBreakTiers.push({
          label: opt,
          minQty: parseQtyBreakMin(opt),
          maxQty: parseQtyBreakMax(opt),
        });
      }
    }
    // Sort by minQty ascending
    qtyBreakTiers.sort(function (a, b) { return a.minQty - b.minQty; });
  }

  function parseQtyBreakMin(label) {
    var cleaned = label.replace(/,/g, '');
    if (/up to/i.test(cleaned)) return 1;
    var match = cleaned.match(/^(\d+)/);
    return match ? parseInt(match[1]) : 1;
  }

  function parseQtyBreakMax(label) {
    var cleaned = label.replace(/,/g, '');
    if (/\+/.test(cleaned)) return Infinity;
    var upToMatch = cleaned.match(/up to\s*(\d+)/i);
    if (upToMatch) return parseInt(upToMatch[1]);
    var rangeMatch = cleaned.match(/to\s*(\d+)/);
    if (rangeMatch) return parseInt(rangeMatch[1]);
    return Infinity;
  }

  function getQtyBreakForQuantity(qty) {
    for (var i = qtyBreakTiers.length - 1; i >= 0; i--) {
      if (qty >= qtyBreakTiers[i].minQty) {
        return qtyBreakTiers[i].label;
      }
    }
    return qtyBreakTiers.length > 0 ? qtyBreakTiers[0].label : '';
  }

  /**
   * Map selected state to pricing tier.
   * TAS, SA, WA have specific pricing. All others use Standard.
   */
  function getStatePricingTier(state) {
    if (['TAS', 'SA', 'WA'].indexOf(state) >= 0) return state;
    return 'Standard';
  }

  function findVariant(colour, qtyBreak, stateTier) {
    // Match all applicable options
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.option1 !== colour) continue;

      if (hasStatePricing && stateOptionKey === 'option3') {
        // 3-option: colour + qty + state
        if (v.option2 === qtyBreak && v.option3 === stateTier) return v;
      } else if (hasStatePricing && stateOptionKey === 'option2') {
        // 2-option: colour + state (no qty break)
        if (v.option2 === stateTier) return v;
      } else if (qtyOptionKey) {
        // No state pricing, has qty break: colour + qty
        if (v.option2 === qtyBreak) return v;
      } else {
        // Single option: just colour
        return v;
      }
    }
    return null;
  }

  function updateVariant() {
    var qtyBreak = qtyOptionKey ? getQtyBreakForQuantity(currentQty) : '';
    var stateTier = hasStatePricing ? getStatePricingTier(currentState) : '';
    var variant = findVariant(currentColour, qtyBreak, stateTier);

    if (!variant) return;

    // Update hidden variant ID input
    var idInput = document.querySelector('input.product-variant-id[name="id"]');
    if (idInput) {
      idInput.value = variant.id;
      if (variant.available) {
        idInput.removeAttribute('disabled');
      } else {
        idInput.setAttribute('disabled', '');
      }
    }

    // Update displayed price (Shopify prices are in cents)
    var priceContainer = document.querySelector('#price-' + getSectionId() + ' .price');
    if (priceContainer) {
      var hasCompareAt = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (hasCompareAt) {
        // Sale display: strikethrough compare_at + sale price + badge
        priceContainer.classList.add('price--on-sale');
        var regularEl = priceContainer.querySelector('.price__sale .price-item--regular');
        if (regularEl) regularEl.textContent = formatMoney(variant.compare_at_price);
        var saleEl = priceContainer.querySelector('.price-item--sale');
        if (saleEl) saleEl.textContent = formatMoney(variant.price);
      } else {
        // Regular display: no strikethrough, no badge
        priceContainer.classList.remove('price--on-sale');
        var regPriceEl = priceContainer.querySelector('.price__regular .price-item--regular');
        if (regPriceEl) regPriceEl.textContent = formatMoney(variant.price);
        var salePriceEl = priceContainer.querySelector('.price-item--sale');
        if (salePriceEl) salePriceEl.textContent = formatMoney(variant.price);
      }
    }

    // Update the dynamic pricing component's base price
    var pricingEl = document.querySelector('allflex-pricing');
    if (pricingEl) {
      pricingEl.basePrice = variant.price / 100;
      pricingEl.dataset.basePrice = (variant.price / 100).toString();

      // Also pass the standard price for rebate display
      if (hasStatePricing && stateTier !== 'Standard') {
        var standardVariant = findVariant(currentColour, qtyBreak, 'Standard');
        if (standardVariant) {
          pricingEl.standardPrice = standardVariant.price / 100;
          pricingEl.dataset.standardPrice = (standardVariant.price / 100).toString();
        }
      } else {
        pricingEl.standardPrice = variant.price / 100;
        pricingEl.dataset.standardPrice = (variant.price / 100).toString();
      }

      // Pass the current state tier for breakdown display
      pricingEl.stateTier = stateTier;
      pricingEl.dataset.stateTier = stateTier;

      if (typeof pricingEl.calculate === 'function') {
        pricingEl.calculate();
      }
    }

    // Update submit button availability
    var submitBtn = document.querySelector('.product-form__submit');
    if (submitBtn) {
      if (variant.available) {
        submitBtn.removeAttribute('disabled');
        submitBtn.querySelector('span').textContent = 'Add to cart';
      } else {
        submitBtn.setAttribute('disabled', '');
        submitBtn.querySelector('span').textContent = 'Sold out';
      }
    }

    // Highlight active tier in volume discount table
    highlightActiveTier(qtyBreak);
  }

  function populateVolumeTiers() {
    var tbody = document.getElementById('volume-discount-tiers');
    if (!tbody || qtyBreakTiers.length === 0) return;

    var colour = currentColour || (variants.length > 0 ? variants[0].option1 : '');
    var stateTier = hasStatePricing ? getStatePricingTier(currentState) : '';
    var html = '';
    for (var i = 0; i < qtyBreakTiers.length; i++) {
      var tier = qtyBreakTiers[i];
      var variant = findVariant(colour, tier.label, stateTier);
      var price = variant ? formatMoney(variant.price) : '-';
      html += '<tr data-tier="' + tier.label + '"><td>' + tier.label + '</td><td>' + price + '</td></tr>';
    }
    tbody.innerHTML = html;
  }

  function highlightActiveTier(activeLabel) {
    var tbody = document.getElementById('volume-discount-tiers');
    if (!tbody) return;
    var rows = tbody.querySelectorAll('tr');
    rows.forEach(function (row) {
      if (row.getAttribute('data-tier') === activeLabel) {
        row.classList.add('allflex-volume-discount__tier--active');
      } else {
        row.classList.remove('allflex-volume-discount__tier--active');
      }
    });
  }

  function getSectionId() {
    var productInfo = document.querySelector('product-info');
    return productInfo ? productInfo.dataset.section : '';
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
